import { useState, useEffect } from 'react';
import { useOpsAuth } from '@/ops/OpsAuthContext';
import { Search, X } from 'lucide-react';

const ACTION_BADGE = {
  login: 'ops-badge-success', logout: 'ops-badge-muted',
  suspend_user: 'ops-badge-danger', delete_user: 'ops-badge-danger', reactivate_user: 'ops-badge-success',
  update_user: 'ops-badge-info', change_plan: 'ops-badge-warning', view_billing: 'ops-badge-warning',
  view_billing_overview: 'ops-badge-warning', request_access: 'ops-badge-info', view_analytics: 'ops-badge-info',
};

export default function OpsLogsPage() {
  const { opsAxios, OPS_API } = useOpsAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async (q = '') => {
    setLoading(true);
    try {
      const res = await opsAxios({ method: 'GET', url: `${OPS_API}/logs`, params: { action: q || undefined, limit: 200 } });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="ops-logs-page">
      <div className="mb-6">
        <h1 className="ops-h1">Audit Log</h1>
        <p className="ops-sub mt-1"><span className="ops-mono" style={{ color: 'var(--ops-text-2)' }}>{total}</span> events · all admin actions recorded</p>
      </div>

      <div className="flex gap-2 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ops-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(search)}
            placeholder="Filter by action…" data-testid="ops-logs-filter" className="ops-input w-full h-9 pl-8 pr-3 text-sm" />
        </div>
        <button onClick={() => load(search)} className="ops-btn ops-btn-secondary h-9 px-3.5">Filter</button>
        {search && <button onClick={() => { setSearch(''); load(''); }} className="ops-btn h-9 px-3" style={{ color: 'var(--ops-muted)' }}>Clear</button>}
      </div>

      <div className="ops-card overflow-hidden">
        <table className="ops-table">
          <thead><tr>{['Timestamp', 'Admin', 'Action', 'Target', 'Reason'].map(h => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center ops-sub" style={{ padding: '40px' }}>Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="text-center ops-sub" style={{ padding: '40px' }}>No logs yet</td></tr>
            ) : logs.map(log => (
              <tr key={log.id}>
                <td className="ops-mono whitespace-nowrap" style={{ color: 'var(--ops-muted)' }}>
                  {new Date(log.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </td>
                <td style={{ color: 'var(--ops-text-2)' }}>{log.admin_email}</td>
                <td><span className={`ops-badge ${ACTION_BADGE[log.action] || 'ops-badge-muted'}`}>{log.action}</span></td>
                <td className="ops-mono" style={{ color: 'var(--ops-muted)' }}>{log.target_user_id ? log.target_user_id.slice(0, 12) + '…' : '—'}</td>
                <td style={{ color: 'var(--ops-muted)' }}>{log.reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
